import type { AdminStory, AdminStoryMedia } from '@erp/shared';
import { Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/index.js';

import { fetchStoryMediaObjectUrl } from './api.js';
import { useObjectUrl } from './useMedia.js';

const IMAGE_MS = 5000;
const VIDEO_FALLBACK_MS = 15000;

/** Slide duration, mirroring the mobile viewer's fallbacks (image 5s, video 15s). */
function durationOf(m: AdminStoryMedia): number {
  return m.durationMs && m.durationMs > 0 ? m.durationMs : m.mediaType === 'video' ? VIDEO_FALLBACK_MS : IMAGE_MS;
}

/** A phone-frame preview that plays a story like the mobile viewer. */
export function StoryPreview({ story }: { story: AdminStory }): JSX.Element {
  const { t } = useTranslation('stories');
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [runId, setRunId] = useState(0); // bumped to restart the active segment's fill
  const videoRef = useRef<HTMLVideoElement>(null);
  const media = story.media;
  const current = media[Math.min(idx, media.length - 1)];
  const isVideo = current?.mediaType === 'video';
  const url = useObjectUrl(
    current ? () => fetchStoryMediaObjectUrl(story.id, current.id, isVideo && current.posterAttachmentId ? 'poster' : undefined) : null,
    current ? `${current.id}:${current.posterAttachmentId ?? ''}` : 'none',
  );

  // Play the active video muted; the progress animation still governs advancement.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) void v.play().catch(() => undefined);
    else v.pause();
  }, [playing, idx, url]);

  const advance = (): void => {
    if (idx + 1 < media.length) { setIdx(idx + 1); setRunId((r) => r + 1); }
    else { setPlaying(false); setFinished(true); }
  };
  const toggle = (): void => {
    if (playing) { setPlaying(false); return; }
    if (finished) { setIdx(0); setFinished(false); setRunId((r) => r + 1); } // replay from the start
    setPlaying(true);
  };
  const scrub = (i: number): void => { setIdx(i); setFinished(false); setRunId((r) => r + 1); };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative aspect-[9/16] w-full max-w-[240px] overflow-hidden rounded-[1.75rem] border-4 border-neutral-900 bg-black shadow-lg">
        {/* progress segments */}
        {media.length > 0 && (
          <div className="absolute inset-x-2 top-2 z-10 flex gap-1">
            {media.map((m, i) => (
              <div key={m.id} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
                {i < idx ? (
                  <div className="h-full w-full bg-white" />
                ) : i === idx ? (
                  <div
                    key={runId}
                    className="h-full bg-white"
                    style={{
                      animationName: 'erp-story-progress',
                      animationDuration: `${durationOf(m)}ms`,
                      animationTimingFunction: 'linear',
                      animationFillMode: 'forwards',
                      animationPlayState: playing ? 'running' : 'paused',
                    }}
                    onAnimationEnd={advance}
                  />
                ) : (
                  <div className="h-full w-0 bg-white" />
                )}
              </div>
            ))}
          </div>
        )}
        {current ? (
          url ? (
            isVideo && !current.posterAttachmentId
              ? <video ref={videoRef} src={url} className="h-full w-full object-cover" muted playsInline loop />
              : <img src={url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full animate-pulse bg-neutral-800" />
          )
        ) : (
          <div className="grid h-full w-full place-items-center px-4 text-center text-xs text-white/50">
            {t('preview.empty', { defaultValue: 'Add media to see the preview' })}
          </div>
        )}
        {/* caption */}
        {current && story.caption && (
          <div className="absolute inset-x-0 bottom-14 z-10 px-3 text-center text-sm font-medium text-white drop-shadow">
            {story.caption}
          </div>
        )}
        {/* tagged product chips */}
        {current && story.products.length > 0 && (
          <div className="absolute inset-x-0 bottom-3 z-10 flex flex-wrap justify-center gap-1 px-2">
            {story.products.slice(0, 3).map((p) => (
              <span key={p.id} className="max-w-[45%] truncate rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-medium text-neutral-900">
                {p.name ?? p.code ?? '—'}
              </span>
            ))}
          </div>
        )}
      </div>
      {media.length > 0 && (
        <Button
          variant="secondary"
          size="sm"
          onClick={toggle}
          aria-label={playing ? t('preview.pause', { defaultValue: 'Pause' }) : t('preview.play', { defaultValue: 'Play' })}
        >
          {playing ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />}
          {playing ? t('preview.pause', { defaultValue: 'Pause' }) : t('preview.play', { defaultValue: 'Play' })}
        </Button>
      )}
      {media.length > 1 && (
        <div className="flex flex-wrap justify-center gap-1">
          {media.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => scrub(i)}
              className={`h-2 w-2 rounded-full ${i === idx ? 'bg-fg' : 'bg-border'}`}
              aria-label={t('preview.slide', { defaultValue: 'Slide {{n}}', n: i + 1 })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
